import { Box, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ChatMessageBubble } from "../../../components/UIToolkit/Chat/ChatMessageBubble";
import { FONT_SIZE, INK, LINE_HEIGHT } from "../../../theme/tokens";
import { BUBBLE_MAX_WIDTH, BUBBLE_SX } from "./consts";
import type { ChatTurnAuthor } from "./customPrintoutDraftState";
import { useCustomPrintoutAuthorLabel } from "./useCustomPrintoutAuthorLabel";

type Props = {
	author: ChatTurnAuthor;
	children: ReactNode;
	/** The author line's test id is this plus `-author`. */
	dataTestId: string;
};

/**
 * A design request in the chat, under who asked for it: "You" for the viewer's own (this session's, or a saved
 * turn of theirs), a colleague's name in the department's name order, or "Former team member" once the account
 * is gone. A saved printout's conversation mixes head nurses, so a request alone doesn't say whose it was.
 */
export const CustomPrintoutRequestBubble: FC<Props> = ({ author, children, dataTestId }) => {
	const { t } = useTranslation();
	const authorLabel = useCustomPrintoutAuthorLabel();
	const label = author.kind === "viewer" ? t("printout.custom.ai.turn.author.you") : authorLabel(author.author);
	return (
		<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
			<Typography data-testid={`${dataTestId}-author`} sx={{ fontSize: FONT_SIZE.CAPTION, lineHeight: LINE_HEIGHT.SNUG, color: INK.MUTED }}>
				{label}
			</Typography>
			<ChatMessageBubble sender="user" maxWidth={BUBBLE_MAX_WIDTH} sx={BUBBLE_SX} dataTestId={dataTestId}>
				{children}
			</ChatMessageBubble>
		</Box>
	);
};
